import VideoBrowserSidebar from "@/components/conduction-social/VideoBrowserSidebar";
import VideoGrid from "@/components/conduction-social/ProjectSubmissionVideoGrid";
import LoginModal from "@/components/modals/LoginModal";
import { SessionContext } from "@/context/SessionContext";
import { ProjectSubmission } from "@/javascript/project-objects";
import {
	ActionIcon,
	Box,
	Card,
	Flex,
	Pagination,
	Stack,
	Text,
	useMantineTheme,
} from "@mantine/core";
import { IconChevronRight, IconFileSad, IconX } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";

const VideoBrowser: React.FC = () => {
	const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
	const [projectSubmissions, setProjectSubmissions] = useState<ProjectSubmission[] | undefined>(undefined);
	const [showLoginModal, setShowLoginModal] = useState<boolean>(true);
	const [filter, setFilter] = useState<"all" | "my-videos" | "my-classroom">("all");
	const [page, setPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(true);

	const theme = useMantineTheme();
	const session = useContext(SessionContext);
	if (!session) throw new Error("Social must be a child of SessionContext.");

	// Reset to page 1 whenever filter changes
	useEffect(() => {
		setPage(1);
	}, [filter]);

	const fetchPage = useCallback(async () => {
		if (!session.user || !session.token) return;

		// Build flags per filter. We only want fully fulfilled (i.e., watchable) videos.
		let approvedOnly = false;
		let unapprovedOnly = false;
		const fulfilledOnly = true;
		let associatedUserId: string | undefined;
		let classroomId: string | undefined;

		if (filter === "my-videos") {
			associatedUserId = session.user.id;
		} else if (filter === "my-classroom") {
			classroomId = session.user.classroomId ?? undefined;
		}

		try {
			setLoading(true);
			setProjectSubmissions(undefined);

			// Signature assumed: (page, approvedOnly, unapprovedOnly, fulfilledOnly, associatedUserId, fulfilledTasksOnly, unfulfilledTasksOnly, classroomId)
			const res = await session.getProjectSubmissionsPage(
				page,
				approvedOnly,
				unapprovedOnly,
				fulfilledOnly,
				associatedUserId,
				false, // fulfilledTasksOnly
				false, // unfulfilledTasksOnly
				classroomId
			);

			setProjectSubmissions(res.items);
			setTotalPages(res.totalPages);
		} finally {
			setLoading(false);
		}
	}, [session, filter, page]);

	// Fetch whenever page or filter changes
	useEffect(() => {
		void fetchPage();
	}, [fetchPage]);

	if (session.user == null) {
		return (
			<>
				<LoginModal showModal={showLoginModal} setShowModal={setShowLoginModal} />
				<Card p={20}>
					<Text>Log in to watch videos.</Text>
				</Card>
			</>
		);
	}

	const sidebarWidth = 300;

	return (
		<Flex w="100%" h="100%" style={{ overflow: "hidden", position: "relative" }}>
			{/* Sidebar */}
			<Box
				style={{
					position: "fixed",
					left: isSidebarOpen ? 0 : -sidebarWidth,
					top: 40,
					width: sidebarWidth,
					height: "100%",
					backgroundColor: theme.colors.blue[6],
					overflowY: "auto",
					zIndex: 100,
					transition: "left 0.3s ease",
				}}
			>
				{/* Close button inside sidebar */}
				{isSidebarOpen && (
					<ActionIcon
						onClick={() => setIsSidebarOpen(false)}
						style={{
							position: "absolute",
							top: 10,
							right: 10,
							zIndex: 110,
							color: theme.white,
						}}
						size="md"
					>
						<IconX />
					</ActionIcon>
				)}

				<VideoBrowserSidebar currentFilter={filter} setFilter={setFilter} />
			</Box>

			{/* Open button when sidebar is closed */}
			{!isSidebarOpen && (
				<ActionIcon
					onClick={() => setIsSidebarOpen(true)}
					style={{
						position: "fixed",
						left: 0,
						top: 60,
						zIndex: 4,
						backgroundColor: theme.colors.blue[7],
						color: theme.white,
					}}
					size="lg"
				>
					<IconChevronRight />
				</ActionIcon>
			)}

			{/* Main content */}
			<Box
				style={{
					marginLeft: isSidebarOpen ? sidebarWidth : 0,
					flex: 1,
					height: "100%",
					overflowY: "auto",
					transition: "margin-left 0.3s ease",
				}}
			>
				<Stack align="center" justify="center">
					<Box w="100%" mt={20}>
						{loading || projectSubmissions === undefined || projectSubmissions.length > 0 ? (
							<VideoGrid projectSubmissions={projectSubmissions} skeletonSize={6} />
						) : (
							<Card w="40%" style={{ backgroundColor: theme.colors.blue[0] }}>
								<IconFileSad size={48} color={theme.colors.red[6]} />
								{filter === "my-videos" ? (
									<Text size="lg" mt={50}>
										You have no videos of your projects yet.
									</Text>
								) : filter === "my-classroom" ? (
									<Text size="lg" mt={50}>
										There are no videos from your classroom yet.
									</Text>
								) : (
									<Text size="lg" mt={50}>
										There are no videos available.
									</Text>
								)}
							</Card>
						)}
					</Box>

					{/* Pagination */}
					{totalPages > 1 && (
						<Pagination
							total={totalPages}
							value={page}
							onChange={setPage}
							withEdges
							size="sm"
							disabled={loading}
						/>
					)}
				</Stack>
			</Box>
		</Flex>
	);
};

export default VideoBrowser;
